<script lang="ts" setup>
  import { createElement } from '@/helper/element-factory'
  import { useBuilderStore } from '@/store/builder'
  import { cloneDeep } from 'lodash'
  import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, PropType, Ref, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  import {
    CONTENT_LIBRARY,
    DEFAULT_PAGE_DIMENSION,
    Direction,
    ElementDropContainer,
    ELEMENTS_LOOKUP,
    IElement,
    OLD_PAGE_HEIGHT,
    OLD_PAGE_WIDTH,
    PAGE_SECTIONS,
    PageElementOptions,
    Rotation,
  } from '@gohighlevel/ghl-proposals-common'

  import { usePropertiesDrawer } from '@/composable/usePropertiesDrawer'
  import { useDocument } from '@/composition'
  import { useActionBarStore } from '@/store/actionBar'
  import { useProductListStore } from '@/store/productListStore'
  import PDFPageBreak from '../common/PDFPageBreak.vue'
  import RenderFloatingComponents from '../common/RenderFloatingComponents.vue'
  import BlockElementsRenderer from './BlockElementsRenderer.vue'
  import CImage from './elements/CImage.vue'
  import CPageBreak from './elements/CPageBreak.vue'
  import CProductList from './elements/CProductList/CProductList.vue'
  import CSignature from './elements/CSignature.vue'
  import CTable from './elements/CTable.vue'
  import CText from './elements/CText.vue'
  import CVideo from './elements/CVideo.vue'
  import PageSection from './PageSection.vue'
  import PageSectionOverlay from './PageSectionOverlay.vue'

  defineComponent({
    CText,
    CVideo,
    CTable,
    CImage,
    CPageBreak,
    CProductList,
    CSignature,
  })

  const props = defineProps({
    elements: {
      type: Array as PropType<Array<IElement>>,
    },
    page: {
      type: Object as PropType<IElement<PageElementOptions>>,
      required: true,
    },
    pageIndex: {
      type: Number,
      required: true,
    },
  })

  const actionBarStore = useActionBarStore()
  const store = useBuilderStore()
  const productListStore = useProductListStore()
  const pageRef = ref<HTMLElement>()
  const pageViewHeight: Ref<number> = ref(0)
  const { documentVersion } = useDocument()

  // Use explicit state from store (updated by store actions)
  // This ensures all page instances share the same reactive state
  const isHeaderSectionActive = computed(() => store.isHeaderSectionActive)
  const isFooterSectionActive = computed(() => store.isFooterSectionActive)

  // Computed property for activeSection (for backward compatibility with PageSectionOverlay)
  const activeSection = computed(() => {
    if (isHeaderSectionActive.value) {
      return PAGE_SECTIONS.HEADERS
    }
    if (isFooterSectionActive.value) {
      return PAGE_SECTIONS.FOOTERS
    }
    return null
  })

  const shouldShowHeaderHint = computed(() => {
    if (!store.isDocumentEditable || !store.headerSelected || isHeaderSectionActive.value) {
      return false
    }
    return true
  })

  const shouldShowFooterHint = computed(() => {
    if (!store.isDocumentEditable || !store.footerSelected || isFooterSectionActive.value) {
      return false
    }
    return true
  })

  // Mouse position tracking for cursor-following tooltip for header/footer hints
  const headerHintMousePosition = ref({ x: 0, y: 0 })
  const showHeaderHintTooltip = ref(false)
  const footerHintMousePosition = ref({ x: 0, y: 0 })
  const showFooterHintTooltip = ref(false)

  const handleHeaderHintMouseMove = (event: MouseEvent) => {
    if (shouldShowHeaderHint.value) {
      headerHintMousePosition.value = { x: event.clientX, y: event.clientY }
      showHeaderHintTooltip.value = true
    }
  }

  const handleHeaderHintMouseLeave = () => {
    showHeaderHintTooltip.value = false
  }

  const handleHeaderHintFocus = () => {
    if (shouldShowHeaderHint.value) showHeaderHintTooltip.value = true
  }

  const handleFooterHintMouseMove = (event: MouseEvent) => {
    if (shouldShowFooterHint.value) {
      footerHintMousePosition.value = { x: event.clientX, y: event.clientY }
      showFooterHintTooltip.value = true
    }
  }

  const handleFooterHintMouseLeave = () => {
    showFooterHintTooltip.value = false
  }

  const handleFooterHintFocus = () => {
    if (shouldShowFooterHint.value) showFooterHintTooltip.value = true
  }

  const shouldShowHeaderActivated = computed(() => {
    return isHeaderSectionActive.value && !!props.page?.component?.options?.headers
  })

  const shouldShowFooterActivated = computed(() => {
    return isFooterSectionActive.value && !!props.page?.component?.options?.footers
  })

  /**
   * Page dimensions
   * @default properties from the page component
   */
  const pageDimensions = computed(() => {
    return (
      props.page?.component?.options?.pageDimensions || {
        dimensions: {
          width: 816,
          height: 1056,
        },
        margins: {
          top: 48,
          right: 48,
          bottom: 48,
          left: 48,
        },
        rotation: Rotation.Portrait,
      }
    )
  })

  const MinPageSectionSize = '48px'

  const isPDFPage = computed(() => props.page?.component.options.fromPDF)
  const pageHeight = computed(() => {
    if (props.page?.version > 1) {
      return pageDimensions.value.dimensions.height + 'px'
    }
    return isPDFPage.value || props.page?.component.options.src ? `${OLD_PAGE_HEIGHT}px` : `${DEFAULT_PAGE_DIMENSION.dimensions.height}px`
  })

  function moveElement(newRowIndex: number, oldRowIndex: number, element: IElement) {
    store.moveElement(cloneDeep(element), props?.page?.id as string, newRowIndex, oldRowIndex)
  }

  const childElementsWithDraggable = computed(() => {
    const element = store.pages.find(x => x.id === props.page?.id)
    const children = element?.children.filter(child => child.component.isDraggable && child.type !== ELEMENTS_LOOKUP.PAGE_BREAK)
    return cloneDeep(children)
  })

  const childComponentsWithoutDraggable = computed(() => {
    const element = store.pages.find(x => x.id === props.page?.id)
    const children = element?.children.filter(child => !child.component.isDraggable && child.type !== ELEMENTS_LOOKUP.PAGE_BREAK)
    return cloneDeep(children)
  })

  const headerComponents = computed(() => {
    const element = store.pages.find(x => x.id === props.page?.id)
    return element?.component?.options?.headers?.children
  })

  const footerComponents = computed(() => {
    const element = store.pages.find(x => x.id === props.page?.id)
    return element?.component?.options?.footers?.children
  })

  function onStart(_ev: MouseEvent, element: IElement) {
    store.setActiveElementId(element.id)
    store.moveElementDragState = true
  }

  function onEnd() {
    store.moveElementDragState = false
  }

  function mouseEnter(e: any, element: IElement) {
    e.preventDefault()
    store.changeHoverState(true)
    const section = e.target?.closest?.('section')
    if (section) section.classList.add('el-active')
    store.updateFocusedElement(element)
  }

  function mouseLeave(e: any) {
    e.preventDefault()
    store.changeHoverState(false)
    const section = e.target?.closest?.('section')
    if (section) section.classList.remove('el-active')
    store.updateFocusedElement()
  }

  const elContainerRef = ref()
  onBeforeUnmount(() => {
    if (elContainerRef.value?.closest) {
      const section = elContainerRef.value?.closest('section')
      if (!section) return
      section.classList.remove('el-active')
      store.changeHoverState(false)
      store.updateFocusedElement()
    }
  })

  const { setOpen } = usePropertiesDrawer()

  watch(elContainerRef, () => {
    if (elContainerRef.value?.length) {
      elContainerRef.value.forEach((section: HTMLDivElement) => {
        if (section?.firstElementChild?.className === 'page-break--el') {
          if (section.parentElement) {
            section.parentElement.style.display = 'none'
          }
        }
      })
    }
  })
  const backgroundSize = computed(() => {
    if (props.page.version > 1) {
      return isPDFPage.value
        ? `${pageDimensions.value.dimensions.width}px ${pageDimensions.value.dimensions.height}px`
        : props.page?.responsiveStyles?.large?.backgroundSize
    } else {
      return isPDFPage.value ? `${OLD_PAGE_WIDTH}px ${OLD_PAGE_HEIGHT}px` : props.page?.responsiveStyles?.large?.backgroundSize
    }
  })
  const pageStyles = computed(() => {
    const page = store.pages.find(x => x.id === props.page?.id)
    return {
      backgroundColor: page?.responsiveStyles?.large?.backgroundColor || null,
      backgroundImage: `url('${page?.component?.options?.src}')`,
      backgroundPosition: page?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: page?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: backgroundSize.value,
      opacity: page?.responsiveStyles?.large?.opacity / 100 || 1,
    }
  })

  const observer = new MutationObserver(mutationsList => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        const currentHeight = pageRef.value?.offsetHeight as number
        if (currentHeight !== pageViewHeight.value) {
          pageViewHeight.value = currentHeight
          measureAndPersistSectionHeights()
        }
      }
    }
  })

  watch(pageRef, newValue => {
    if (newValue) {
      observer.observe(pageRef.value as HTMLElement, {
        attributes: true,
        childList: true,
        subtree: true,
      })
    }
  })

  onMounted(() => {
    pageViewHeight.value = pageRef.value?.offsetHeight as number
    if (pageRef.value) {
      observer.observe(pageRef.value, {
        attributes: true,
        childList: true,
        subtree: true,
      })
    }

    if (props.page?.component?.options?.headers) {
      store.updateHeaderSelectedFlag(true)
    }

    if (props.page?.component?.options?.footers) {
      store.updateFooterSelectedFlag(true)
    }

    nextTick(measureAndPersistSectionHeights)
  })

  watch(
    () => store.activeElementId,
    () => nextTick(measureAndPersistSectionHeights),
    { flush: 'post' }
  )

  const headerStyles = computed(() => {
    const page = store.pages.find(x => x.id === props.page?.id)
    const section = page?.component?.options[PAGE_SECTIONS.HEADERS]
    return {
      backgroundColor: section?.responsiveStyles?.large?.backgroundColor || null,
      backgroundImage: `url('${section?.component?.options?.src}')`,
      backgroundPosition: section?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: section?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: '100%',
      opacity: section?.responsiveStyles?.large?.opacity / 100 || 1,
    }
  })

  const footerStyles = computed(() => {
    const page = store.pages.find(x => x.id === props.page?.id)
    const section = page?.component?.options[PAGE_SECTIONS.FOOTERS]
    return {
      backgroundColor: section?.responsiveStyles?.large?.backgroundColor || null,
      backgroundImage: `url('${section?.component?.options?.src}')`,
      backgroundPosition: section?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: section?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: '100%',
      opacity: section?.responsiveStyles?.large?.opacity / 100 || 1,
    }
  })

  const onHeaderDblClick = (event: MouseEvent) => {
    event.stopImmediatePropagation()
    if (store.openHeaderSectionForEditing()) setOpen()
  }

  const onFooterDblClick = (event: MouseEvent) => {
    event.stopImmediatePropagation()
    if (store.openFooterSectionForEditing()) setOpen()
  }

  const updateActiveElement = (event: MouseEvent, element: IElement | null) => {
    event.stopImmediatePropagation()

    // If clicking on elements drawer or the add element button and a header/footer section is active, don't deactivate
    if (!element) {
      const elementsDrawer = document.querySelector('#elements-drawer')
      const addElementButton = document.querySelector('#btn_elements')
      const activeElement = store.activeElement
      const isClickingElementsDrawer = elementsDrawer && elementsDrawer.contains(event.target as Node)
      const isClickingAddButton = addElementButton && (addElementButton.contains(event.target as Node) || addElementButton === event.target)
      if (isClickingElementsDrawer || isClickingAddButton) {
        if (activeElement) {
          // Check if active element IS a header/footer section
          const isSection = activeElement.type === ELEMENTS_LOOKUP.PAGE_SECTION
          // Check if active element is WITHIN a header/footer section
          const elementSection = store.getElementSection(activeElement.id)
          const isInHeaderOrFooter = elementSection === PAGE_SECTIONS.HEADERS || elementSection === PAGE_SECTIONS.FOOTERS
          if (isSection || isInHeaderOrFooter) {
            return
          }
        }
      }
    }

    store.updateActiveElement(element)
    productListStore.setActiveRow(null, null)
    store.moveElementDragState = false
    actionBarStore.dragStateIsActivated = false
    activatePaymentSettings(element)
    actionBarStore.setFloatingElementDragging(false)

    // Section activation is now handled by computed properties
  }

  const snapContainerClass = computed(() => `snapContainer-${props.pageIndex}`)
  const boundContainerClass = computed(() => `boundContainer-${props.pageIndex}`)
  const containerRef = ref()
  const headerRef = ref<HTMLElement>()
  const footerRef = ref<HTMLElement>()

  /** Current page from store (for reactive headerHeight/footerHeight used by PDFPageBreak) */
  const currentPageFromStore = computed(() => store.pages.find(p => p.id === props.page?.id))

  /** Content-only height (excludes header and footer) so PDF page-break positions match the PDF. */
  const pageContentHeightForBreaks = computed(() => {
    const total = pageViewHeight.value || 0
    if (total <= 0) return 0
    const hasHeader = !!props.page?.component?.options?.headers
    const hasFooter = !!props.page?.component?.options?.footers
    const headerH = currentPageFromStore.value?.component?.options?.headerHeight ?? 48
    const footerH = currentPageFromStore.value?.component?.options?.footerHeight ?? 48
    const top = hasHeader ? headerH : (pageDimensions.value.margins?.top ?? 48)
    const bottom = hasFooter ? footerH : (pageDimensions.value.margins?.bottom ?? 48)
    return Math.max(0, total - top - bottom)
  })

  /** Measure header/footer heights and persist to page options for PDF page-break calculation */
  const measureAndPersistSectionHeights = () => {
    if (!props.page?.id) return
    let headerHeight: number | undefined
    let footerHeight: number | undefined
    if (headerRef.value && (props.page?.component?.options?.headers || headerRef.value.offsetHeight > 0)) {
      headerHeight = headerRef.value.offsetHeight || headerRef.value.getBoundingClientRect().height
    }
    if (footerRef.value && (props.page?.component?.options?.footers || footerRef.value.offsetHeight > 0)) {
      footerHeight = footerRef.value.offsetHeight || footerRef.value.getBoundingClientRect().height
    }
    if (headerHeight !== undefined || footerHeight !== undefined) {
      store.updatePageSectionHeights(props.page.id, { headerHeight, footerHeight })
    }
  }

  const onDropContentLibrary = (evt: any, idx: number, colIndex: number) => {
    const content = evt.dataTransfer.getData('content')
    const contentData = JSON.parse(content)
    actionBarStore.setDragState(false)
    store.addElementsFromContentLibrary(
      {
        pages: contentData.pages,
        groups: contentData.groups || [],
        customValueLinkage: contentData.customValueLinkage || {},
      },
      props.page?.id,
      idx as number,
      colIndex as number
    )
  }

  const onDrop = (evt: any, idx: number, colIndex: number, rowColIndex: number) => {
    const containerReact = containerRef?.value?.getBoundingClientRect()
    if (!containerReact) {
      // console.error('no containerRect found.', containerReact)
      return
    }
    // Calculate position relative to the content area's top-left corner
    // containerRef is the content div, so containerReact gives us the content area's bounding rect
    // evt.clientX and evt.clientY are viewport coordinates
    // We need to subtract the container's position to get coordinates relative to the content area
    const left = evt.clientX - containerReact.left
    // For top: evt.clientY is the mouse Y in viewport, containerReact.top is content area top in viewport
    // Subtracting gives us position relative to content area (0,0 at content area top-left)
    // This already accounts for header height since containerReact.top includes it
    const top = evt.clientY - containerReact.top
    const bottom = containerReact.height - top

    const type = evt.dataTransfer.getData('name')

    if (!props.page) return
    if (type === ELEMENTS_LOOKUP.PAGE_BREAK && (colIndex > 0 || rowColIndex > 0)) {
      return
    }
    const el: IElement = createElement({
      type,
      documentVersion: documentVersion.value,
    }) as IElement
    if (el) {
      store.addElement(
        cloneDeep({
          ...el,
          responsiveStyles: {
            ...el.responsiveStyles,
            large: {
              ...el.responsiveStyles.large,
              scale: { scaleX: 1, scaleY: 1 },
              position: { top, left, bottom, right: 0 },
            },
          },
        }),
        props.page?.id,
        idx as number,
        colIndex as number,
        rowColIndex as number
      )
    }
  }

  const getAdjustedRowIndex = (oldIdx: number, newIdx: number, length: number) => {
    if (oldIdx < 0) return newIdx
    if (oldIdx === 0) return newIdx - 1
    if (oldIdx < newIdx && length === 1) {
      return newIdx - 1 > -1 ? newIdx - 1 : 0
    }
    if (oldIdx < newIdx) {
      return newIdx - 1
    }
    return newIdx
  }

  const getAdjustedColIndex = (oldIdx: number, newIdx: number, currentRowId: string, newRowId: string, length: number) => {
    if (currentRowId === newRowId && oldIdx < newIdx && length === 1) {
      return newIdx - 1 > -1 ? newIdx - 1 : 0
    }
    if (oldIdx < 0) return newIdx

    return newIdx
  }
  const getInfoForCurrentElement = (id: string) => {
    const foundPage = store.pages?.find(x => x.id === props.page?.id)
    if (!foundPage) {
      // Handle the case where no page is found
      console.error('Page not found')
      return
    }

    const rows = foundPage.children?.filter(x => x.type === ELEMENTS_LOOKUP.ROW)
    if (!rows) {
      console.error('No rows found')
      return
    }

    return rows.reduce(
      (acc, row) => {
        if (!row.children) return acc

        row.children.forEach((col, index) => {
          if (!col.children) return acc

          col.children.forEach(el => {
            if (el.id === id) {
              acc.colIdx = index
              acc.currentRowId = row.id
              acc.colChildLength = col.children.length
            }
          })
        })

        return acc
      },
      { currentRowId: '', colIdx: -1, colChildLength: 0 }
    )
  }
  const onDropElement = (
    ev: DragEvent,
    option: {
      rowIndex?: number
      dir: Direction
      colIndex?: number
      rowColIndex?: number
      currentPageId?: string
      rowId?: string
    }
  ) => {
    ev.preventDefault()
    const type = ev?.dataTransfer?.getData('name')
    if (type === CONTENT_LIBRARY) {
      onDropContentLibrary(ev, option.rowIndex as number, option.colIndex as number)
      return
    }
    actionBarStore.toggleDragState(false)
    store.moveElementDragState = false
    const action = ev?.dataTransfer?.getData('action')
    if (action === 'add') {
      onDrop(ev, option.rowIndex as number, option.colIndex as number, option.rowColIndex as number)
    } else {
      const existingElement = actionBarStore.lastActiveDragElement
      if (existingElement) {
        const pageId = store.getPageIdByElementId(existingElement?.id)
        if (pageId) {
          // create row if not created and push element here
          const el = cloneDeep(existingElement)
          if (el) {
            const currentIdx = store.pages.find(x => x.id === props.page?.id)?.children.findIndex(x => x.id === existingElement.id)
            const info = getInfoForCurrentElement(existingElement.id)
            if (info) {
              const { currentRowId, colIdx, colChildLength } = info
              store.deleteElement(existingElement?.id)
              store.addElement(
                el,
                option.currentPageId as string,
                getAdjustedRowIndex(currentIdx as number, option.rowIndex as number, colChildLength),
                getAdjustedColIndex(colIdx, option.colIndex as number, currentRowId, option.rowId as string, colChildLength),
                option.rowColIndex as number
              )
            }
          }
        } else {
          const currentIdx = store.pages.find(x => x.id === props.page?.id)?.children.findIndex(x => x.id === store.activeElementId)
          if (currentIdx !== option.rowIndex) {
            moveElement(option.rowIndex as number, currentIdx as number, existingElement)
          }
        }
      }
    }
  }

  const activatePaymentSettings = (element: IElement | null) => {
    if (!store.isDocumentEditable) return
    if (element && element.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
      actionBarStore.setPaymentSettings(true)
    }
  }

  const { t } = useI18n()
</script>

<template>
  <div class="elements_page bg-white shadow-page" ref="pageRef">
    <div
      class="page-view clearfix"
      :class="{
        [boundContainerClass]: true,
      }"
      :style="{
        minHeight: pageHeight,
        width: pageDimensions.dimensions.width + 'px',
      }"
    >
      <PDFPageBreak
        v-if="pageViewHeight"
        :parent-height="pageContentHeightForBreaks"
        :page-dimensions="pageDimensions"
        :page-version="props.page.version"
        :has-header="!!props.page?.component?.options?.headers"
        :has-footer="!!props.page?.component?.options?.footers"
        :header-height="currentPageFromStore?.component?.options?.headerHeight"
        :footer-height="currentPageFromStore?.component?.options?.footerHeight"
      />
      <div
        ref="headerRef"
        class="page-header header-footer clearfix"
        :class="{
          'section-active': shouldShowHeaderActivated,
          'section-enabled': store.headerSelected,
          'section-disabled': !store.headerSelected && store.isDocumentEditable,
        }"
        :data-hint="store.isDocumentEditable && !isHeaderSectionActive ? t('common.doubleClickToEditHeader') : undefined"
        :style="{
          minHeight: store.headerSelected ? MinPageSectionSize : '48px',
          position: 'relative',
          ...headerStyles,
        }"
        @dblclick.capture="onHeaderDblClick"
      >
        <div
          class="page-header-content"
          tabindex="0"
          @mousemove="handleHeaderHintMouseMove"
          @mouseleave="handleHeaderHintMouseLeave"
          @focus="handleHeaderHintFocus"
          @blur="handleHeaderHintMouseLeave"
          :style="{
            marginTop: '0',
            marginBottom: '0',
            marginLeft: pageDimensions.margins.left + 'px',
            marginRight: pageDimensions.margins.right + 'px',
            ...(store.headerSelected
              ? {
                  minHeight: MinPageSectionSize,
                  height: '100%',
                }
              : {
                  minHeight: '48px',
                  boxSizing: 'border-box',
                }),
          }"
        >
          <div
            v-if="shouldShowHeaderHint && showHeaderHintTooltip"
            class="fixed pointer-events-none z-[9999]"
            :style="{
              left: `${headerHintMousePosition.x + 10}px`,
              top: `${headerHintMousePosition.y + 10}px`,
            }"
          >
            <div class="bg-gray-700 text-white px-3 py-2 rounded text-xs whitespace-nowrap shadow-lg select-none">
              {{ t('common.doubleClickToEditHeader') }}
            </div>
          </div>
          <PageSection
            v-if="store.headerSelected"
            :margins="pageDimensions.margins"
            :margin-top="'0'"
            :elements="headerComponents ?? []"
            :page-index="props.pageIndex"
            :parent-page-id="props.page?.id"
            :section-id="props.page?.id"
            :section-type="PAGE_SECTIONS.HEADERS"
          />
          <PageSectionOverlay v-if="shouldShowHeaderActivated" section-type="headers" :active-section="activeSection" />
        </div>
      </div>
      <div
        ref="containerRef"
        class="page-content"
        :id="props.page?.id"
        :class="{
          [snapContainerClass]: true,
          [boundContainerClass]: true,
        }"
        :style="{
          position: 'relative',
          marginLeft: pageDimensions.margins.left + 'px',
          marginRight: pageDimensions.margins.right + 'px',
        }"
        :data-id="props.page?.id"
        :data-type="ELEMENTS_LOOKUP.PAGE"
      >
        <RenderFloatingComponents
          v-if="props.page?.children"
          :page-id="props.page?.id"
          :parent-id="props.page?.id as string"
          :page-index="props.pageIndex"
          :child-components-with-draggable="childElementsWithDraggable as IElement[]"
          :snap-container-class="snapContainerClass"
          :bound-container-class="boundContainerClass"
          :key="`page-level-key-${props.page?.id}`"
        />

        <div class="relative draggable-container clearfix flex flex-col" style="width: 100%">
          <div
            v-if="actionBarStore.draggingFloatingElement"
            class="absolute w-full h-full top-0 left-0 z-[100]"
            @dragover="
              event => {
                event.preventDefault()
              }
            "
            @drop="
              event =>
                onDropElement(event, {
                  dir: 'bottom',
                  rowIndex: page.children?.length,
                })
            "
          />
          <ElementDropContainer
            v-if="childComponentsWithoutDraggable?.length === 0"
            :idx="0"
            :for-initial="true"
            @onDropElement="onDropElement"
            width="100%"
            :page-id="props.page?.id"
            :col-index="0"
            :row-index="0"
          />
          <div v-else tabindex="0" @mouseover="mouseEnter" @mouseleave="mouseLeave" @focusin="mouseEnter" @focusout="mouseLeave">
            <BlockElementsRenderer
              :key="`BlockElementsRenderer-page-level-key-${props.page?.id}`"
              :elements="childComponentsWithoutDraggable as IElement[]"
              @on-move-start="onStart"
              @on-move-end="onEnd"
              @click="(...args) => updateActiveElement(...args)"
              @on-drop-element="onDropElement"
              :page-id="props.page?.id"
              :drag-enabled="(store.moveElementDragState || actionBarStore.dragStateIsActivated) && !actionBarStore.draggingFloatingElement"
              :parent-id="props.page?.id as string"
              :page-index="props.pageIndex"
              :snap-container-class="snapContainerClass"
              :bound-container-class="boundContainerClass"
            />
          </div>
          <span
            v-if="
              childComponentsWithoutDraggable?.length > 0 &&
              (store.moveElementDragState || actionBarStore.dragStateIsActivated) &&
              !actionBarStore.draggingFloatingElement
            "
            :ondragover="event => event.preventDefault()"
            :ondrop="
              (ev: DragEvent) =>
                onDropElement(ev, { dir: 'bottom', rowIndex: childComponentsWithoutDraggable?.length, currentPageId: props.page?.id })
            "
            class="relative block bg-transparent bottom-0 left-0 w-full z-0 flex-1"
          />
        </div>
      </div>

      <div
        ref="footerRef"
        class="page-footer header-footer clearfix"
        :class="{
          'section-active': shouldShowFooterActivated,
          'section-enabled': store.footerSelected,
          'section-disabled': !store.footerSelected && store.isDocumentEditable,
        }"
        :data-hint="store.isDocumentEditable && !isFooterSectionActive ? t('common.doubleClickToEditFooter') : undefined"
        :style="{
          minHeight: store.footerSelected ? MinPageSectionSize : '48px',
          position: 'relative',
          ...footerStyles,
        }"
        @dblclick.capture="onFooterDblClick"
      >
        <div
          class="page-footer-content"
          tabindex="0"
          @mousemove="handleFooterHintMouseMove"
          @mouseleave="handleFooterHintMouseLeave"
          @focus="handleFooterHintFocus"
          @blur="handleFooterHintMouseLeave"
          :style="{
            marginTop: '0',
            marginBottom: '0',
            marginLeft: pageDimensions.margins.left + 'px',
            marginRight: pageDimensions.margins.right + 'px',
            ...(store.footerSelected
              ? {
                  minHeight: MinPageSectionSize,
                  height: '100%',
                }
              : {
                  minHeight: '48px',
                  boxSizing: 'border-box',
                }),
          }"
        >
          <div
            v-if="shouldShowFooterHint && showFooterHintTooltip"
            class="fixed pointer-events-none z-[9999]"
            :style="{
              left: `${footerHintMousePosition.x + 10}px`,
              top: `${footerHintMousePosition.y + 10}px`,
            }"
          >
            <div class="bg-gray-700 text-white px-3 py-2 rounded text-xs whitespace-nowrap shadow-lg select-none">
              {{ t('common.doubleClickToEditFooter') }}
            </div>
          </div>
          <PageSectionOverlay v-if="shouldShowFooterActivated" section-type="footers" :active-section="activeSection" />
          <PageSection
            v-if="store.footerSelected"
            :margins="pageDimensions.margins"
            :margin-top="'0'"
            :elements="footerComponents ?? []"
            :page-index="props.pageIndex"
            :parent-page-id="props.page?.id"
            :section-id="props.page?.id"
            :section-type="PAGE_SECTIONS.FOOTERS"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .flip-list-move {
    transition: transform 0.5s;
  }

  .no-move {
    transition: transform 0s;
  }

  .elements_page {
    position: relative;
    &::before {
      position: absolute;
      content: '';
      z-index: 0;
      width: 100%;
      height: 100%;
      will-change: transform;
      transform: translateZ(0px);
      top: 0;
      background-color: v-bind('pageStyles.backgroundColor');
      background-image: v-bind('pageStyles.backgroundImage');
      background-position: v-bind('pageStyles.backgroundPosition');
      background-repeat: v-bind('pageStyles.backgroundRepeat');
      background-size: v-bind('pageStyles.backgroundSize');
      opacity: v-bind('pageStyles.opacity');
    }
  }

  .section-active {
    border: 2px solid #3b82f6;
    border-radius: 4px;
    transition: border-color 0.2s ease-in-out;
    /* When header/footer is being edited, bring it above page-content (z-index: 1). */
    z-index: 50;
  }

  .header-footer {
    &.section-disabled {
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      // background-color: rgba(243, 244, 246, 0.5);
      // border: 1px dashed #d1d5db;

      &:hover {
        background-color: rgba(59, 130, 246, 0.1);
        border-color: #3b82f6;

        &::after {
          content: attr(data-hint);
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background-color: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 10px;
          white-space: nowrap;
          pointer-events: none;
          z-index: 10;
          letter-spacing: -0.01em;
        }
      }
    }

    .page-header-content,
    .page-footer-content {
      position: relative;
    }
  }
</style>

<style lang="scss">
  .clearfix {
    &::after {
      content: '';
      display: table;
      clear: both;
    }
  }
  .page-view {
    position: relative;
    display: flex;
    flex-direction: column;
    &::after {
      content: '';
      display: table;
      clear: both;
    }
  }
  .page-header {
    .page-header-content {
      box-sizing: border-box;
    }
  }

  .header-footer {
    position: relative;
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    /* Keep header/footer stacking context below page-content by default so
       overlays from main content sit above header/footer floating fields. */
    z-index: 0;
  }

  .page-footer {
    .page-footer-content {
      box-sizing: border-box;
    }
  }

  .page-content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    position: relative;
    /* Ensure main content overlays (table menu, element actions) sit above
       header/footer floating fields that overlap visually when overlays
       extend into header/footer area. */
    z-index: 1;
    &--floating-active {
      z-index: 10;
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: transparent;
    }
  }

  .draggable-container {
    flex: 1 0 auto;
  }
</style>
